import { IComponentOptions, IQService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalInstanceService, IModalService } from "angular-ui-bootstrap";
import { GtmhubController, IRestLayerRequest } from "@gtmhub/core";
import { IWhiteboard } from "../models";
import { WhiteboardsService } from "../whiteboards-service";
import { whiteboardState } from "../whiteboards.states";
import { getCurrentUserId, hasCurrentUserRole } from "@gtmhub/users";
import { IAssigneeIdMap, AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { WhiteboardsUserSettingsService } from "../whiteboards-user-settings-service";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { HowToStartModalOpenerService } from "../../shared/services/how-to-start-modal-opener.service";
import { localize } from "@gtmhub/localization";
import { capitalize } from "@gtmhub/util";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";

type Paginator = {
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
};

const bindStateToCtrl = (state: IAssigneesStoreState) => {
  return {
    assignees: state.assignees.map,
  };
};

export class WhiteboardsComponentCtrl extends GtmhubController {
  myWhiteboards: IWhiteboard[] = [];
  sharedWhiteboards: IWhiteboard[];
  assignees: IAssigneeIdMap;
  myWhiteboardsIndicators: {
    loading?: IIndicator;
  };
  sharedWhiteboardsIndicators: {
    loading?: IIndicator;
  };
  infoBoxesId: string;
  myWhiteboardsPaginator: Paginator;
  sharedWhiteboardsPaginator: Paginator;

  private currentUserId: string;
  private checkingEnabledFeatures: boolean;
  private showNewExperience: boolean;
  private confirmDeleteModal: IModalInstanceService;
  public favoritesManager: FavoritesManager;

  static $inject = [
    "$state",
    "WhiteboardsService",
    "$ngRedux",
    "AssigneeActions",
    "UIErrorHandlingService",
    "$uibModal",
    "WhiteboardsUserSettingsService",
    "AnalyticsService",
    "$rootScope",
    "HowToStartModalOpenerService",
    "FavoritesManagerFactory",
    "FeatureToggleService",
    "AccountService",
    "$q",
  ];

  constructor(
    private $state: StateService,
    private whiteboardsService: WhiteboardsService,
    $ngRedux: INgRedux,
    assigneeActions: AssigneeActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $uibModal: IModalService,
    private whiteboardsUserSettingsService: WhiteboardsUserSettingsService,
    private analyticsService: AnalyticsService,
    private $rootScope: ITraceRootScopeService,
    private howToStartModalOpenerService: HowToStartModalOpenerService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private featureToggleService: FeatureToggleService,
    private accountService: AccountService,
    private $q: IQService
  ) {
    super();

    this.myWhiteboardsIndicators = {};
    this.sharedWhiteboardsIndicators = {};
    this.currentUserId = getCurrentUserId();

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(assigneeActions.getAssignees());

    this.favoritesManager = this.favoritesManagerFactory(["whiteboard"]);
  }

  $onInit(): void {
    this.myWhiteboardsIndicators.loading = { progress: true };
    this.sharedWhiteboardsIndicators.loading = { progress: true };
    this.infoBoxesId = hasCurrentUserRole("admin") ? "whiteboardsAdmin" : "whiteboardsBasicUser";

    this.setDefaultPaginators();
    this.getMyWhiteboards();
    this.getSharedWhiteboards();

    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;
    const promises = {
      newExperienceEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperience),
      newExperienceToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
    };

    this.$q
      .all(promises)
      .then(({ newExperienceEnabled, newExperienceToggleEnabled }) => {
        const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
        this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
        this.checkingEnabledFeatures = false;
        if (this.showNewExperience) {
          this.favoritesManager.initFavorites();
        }
      })
      .catch(() => (this.checkingEnabledFeatures = false));
  }

  onMyWhiteboardsPageChange(page: number): void {
    this.$rootScope.traceAction("change_my_whiteboards_page", () => {
      this.myWhiteboardsPaginator.currentPage = page;
      this.getMyWhiteboards();
    });
  }

  onSharedWhiteboardsPageChange(page: number): void {
    this.$rootScope.traceAction("change_shared_whiteboards_page", () => {
      this.sharedWhiteboardsPaginator.currentPage = page;
      this.getSharedWhiteboards();
    });
  }

  createNew(): void {
    this.$rootScope.traceAction("create_new_whiteboard", () => {
      const wb: Partial<IWhiteboard> = { name: capitalize(localize("new_whiteboard")) };

      this.whiteboardsService.createNew(wb, { created_from_template: false }).then(
        (whiteboard) => {
          this.view(whiteboard);
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  }

  openTemplates(): void {
    this.$uibModal.open({
      template: "<whiteboard-templates close='$close()'></whiteboard-templates>",
      windowClass: "medium wb-templates-modal",
      backdropClass: "dark-backdrop",
    });
  }

  openSelectHowToStartModal(): void {
    this.howToStartModalOpenerService.openHowToStartModal([
      {
        title: "start_with_template",
        text: "start_with_ready_made_templates",
        img: require("wwwroot/img/empty-states/whiteboards/start-with-template.svg"),
        button: {
          actionCb: () => {
            this.analyticsService.track("Create First Whiteboard Clicked", { how_to_start: "with a template" });
            this.openTemplates();
          },
          kind: "primary",
        },
      },
      {
        title: "start_with_empty_board",
        text: "draft_okrs_sticky_notes_and_more",
        img: require("wwwroot/img/empty-states/whiteboards/start-with-empty-board.svg"),
        button: {
          actionCb: () => {
            this.analyticsService.track("Create First Whiteboard Clicked", { how_to_start: "on your own" });
            this.createNew();
          },
          kind: "secondary",
        },
      },
    ]);
  }

  view(whiteboard: IWhiteboard): void {
    this.$state.go(whiteboardState.name, { whiteboardId: whiteboard.id });
  }

  clone(whiteboardId: string): void {
    this.$rootScope.traceAction("clone_whiteboard", () => {
      this.whiteboardsService.clone(whiteboardId).then(
        (whiteboard) => this.view(whiteboard),
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  }

  getNavItem = (whiteboard: IWhiteboard): INavItem => {
    return this.favoritesManager.getFavoriteItem({ id: whiteboard.id }) || navItemsFactory.withRandomColor(navItemsFactory.buildWhiteboardNavItem(whiteboard));
  };

  whiteboardIsFavorite = (whiteboard: IWhiteboard): boolean => {
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite(whiteboard);
  };

  isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };

  isShared(whiteboard: IWhiteboard): boolean {
    return whiteboard.createdById !== this.currentUserId;
  }

  delete(whiteboard: IWhiteboard): void {
    this.showConfirmDeleteModal(whiteboard);
  }

  private showConfirmDeleteModal = (whiteboard) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeleteModal = this.$uibModal.open({
      templateUrl: require("./confirm-delete.jade"),
      controller: [
        "$scope",
        function ($scope) {
          $scope.close = () => {
            ctrl.confirmDeleteModal.close();
          };
          $scope.delete = () => {
            ctrl.$rootScope.traceAction("delete_whiteboard", () => {
              ctrl.whiteboardsService.delete(whiteboard).then(
                () => {
                  ctrl.myWhiteboards = ctrl.myWhiteboards.filter((w) => w.id !== whiteboard.id);
                  ctrl.whiteboardsUserSettingsService.remove(whiteboard.id);
                  ctrl.confirmDeleteModal.close();
                },
                (error) => ctrl.uiErrorHandlingService.handleModal(error)
              );
            });
          };
        },
      ],
    });
  };

  private getSharedWhiteboards(): void {
    this.sharedWhiteboardsIndicators.loading = { progress: true };
    const queryParams: IRestLayerRequest = {
      skip: (this.sharedWhiteboardsPaginator.currentPage - 1) * this.sharedWhiteboardsPaginator.itemsPerPage,
      limit: this.sharedWhiteboardsPaginator.itemsPerPage,
      fields: "access,ownerId,name,picture,createdAt,createdById,lastModifiedAt",
      filter: {
        $and: [{ ownerId: { $ne: this.currentUserId } }],
      },
      sort: "-lastModifiedAt,-createdAt",
    };

    this.whiteboardsService
      .get(queryParams)
      .then((result) => {
        this.sharedWhiteboards = result.items;
        this.sharedWhiteboardsPaginator.totalPages = Math.ceil(result.totalCount / this.sharedWhiteboardsPaginator.itemsPerPage);
        delete this.sharedWhiteboardsIndicators.loading;
      })
      .catch((error) => {
        this.sharedWhiteboardsIndicators.loading = { error };
      });
  }
  private setDefaultPaginators(): void {
    this.myWhiteboardsPaginator = {
      currentPage: 1,
      itemsPerPage: 15,
      totalPages: 0,
    };

    this.sharedWhiteboardsPaginator = {
      currentPage: 1,
      itemsPerPage: 15,
      totalPages: 0,
    };
  }

  private getMyWhiteboards(): void {
    this.myWhiteboardsIndicators.loading = { progress: true };
    const queryParams: IRestLayerRequest = {
      skip: (this.myWhiteboardsPaginator.currentPage - 1) * this.myWhiteboardsPaginator.itemsPerPage,
      limit: this.myWhiteboardsPaginator.itemsPerPage,
      fields: "access,ownerId,name,picture,createdAt,createdById,lastModifiedAt",
      filter: {
        $and: [{ ownerId: this.currentUserId }],
      },
      sort: "-lastModifiedAt,-createdAt",
    };
    this.whiteboardsService
      .get(queryParams)
      .then((result) => {
        this.myWhiteboards = result.items;
        this.myWhiteboardsPaginator.totalPages = Math.ceil(result.totalCount / this.myWhiteboardsPaginator.itemsPerPage);
        delete this.myWhiteboardsIndicators.loading;
      })
      .catch((error) => {
        this.myWhiteboardsIndicators.loading = { error };
      });
  }
}

export const WhiteboardsComponent: IComponentOptions = {
  controller: WhiteboardsComponentCtrl,
  templateUrl: require("./whiteboards.jade"),
};
